import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '../../../lib/utils';

const STORAGE_PREFIX = '@eureca/ui:table';
// No floor on resize width — cells no longer clip, so a 0px column is just
// an invisible column with its content spilling over. `0` is also the
// natural clamp against a drag that travels past the column's left edge.
const MIN_RESIZABLE_WIDTH = 0;

// JSX renders strings and numbers as text nodes; everything else (elements,
// fragments, arrays, null) we treat as "rich content" and leave untouched.
function isTextLike(node: ReactNode): boolean {
  return typeof node === 'string' || typeof node === 'number';
}

// Widths for every resizable column in one Table live together in context so
// that a drag on column A can "freeze" the rendered widths of B, C, … into
// explicit pixel values before A starts growing. Without that freeze, the
// browser would redistribute the remaining space (table-fixed + w-full) and
// shrink the siblings — exactly what we don't want.
type TableStorageState = {
  storageId: string | null;
  widths: Record<string, number>;
  hasFrozenWidths: boolean;
  setColumnWidth: (colKey: string, width: number) => void;
  freezeColumnsFromDOM: () => void;
};

const TableStorageContext = createContext<TableStorageState | null>(null);

function readAllStoredWidths(storageId: string | null): Record<string, number> {
  if (!storageId) return {};
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${storageId}`);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value) && value >= MIN_RESIZABLE_WIDTH) {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeAllStoredWidths(storageId: string | null, widths: Record<string, number>): void {
  if (!storageId) return;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}:${storageId}`, JSON.stringify(widths));
  } catch {
    // Swallow — private-browsing / quota / disabled storage shouldn't break
    // the resize itself. The width still applies in-memory for the session.
  }
}

// Sync the native `title` attribute on enter so the browser tooltip exposes
// the full text only for cells whose content is actually truncated by
// `overflow-hidden + text-ellipsis`. Done lazily on hover (no ResizeObserver
// per cell) to keep large tables cheap; `textContent` flattens nested buttons
// / spans / badges into the same string the user sees.
function syncOverflowTitle(el: HTMLElement): void {
  if (el.scrollWidth > el.clientWidth) {
    el.setAttribute('title', el.textContent ?? '');
  } else {
    el.removeAttribute('title');
  }
}

function assignRef<T>(ref: Ref<T> | undefined, node: T | null): void {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    (ref as { current: T | null }).current = node;
  }
}

// Walk up the DOM tree until we find an ancestor that actually scrolls
// horizontally. Used by the resize-drag logic to drive auto-scroll when the
// cursor reaches the wrapper's edge — without that, dragging a column wider
// than the viewport silently grows it off-screen.
function findHorizontalScrollAncestor(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const overflowX = window.getComputedStyle(node).overflowX;
    if (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') {
      return node;
    }
    // Stop at the document root — keeps the lookup bounded.
    if (node === document.documentElement) return null;
    node = node.parentElement;
  }
  return null;
}

/**
 * Composable table primitive — a thin Tailwind layer over native `<table>`.
 *
 * Pattern matches shadcn/ui: each part is a forwardRef'd component that
 * accepts native attrs + className. Compose them top-down:
 *
 *   <Table>
 *     <TableHeader>
 *       <TableRow>
 *         <TableHead>ID</TableHead>
 *         <TableHead>Nome</TableHead>
 *       </TableRow>
 *     </TableHeader>
 *     <TableBody>
 *       <TableRow>
 *         <TableCell>00001</TableCell>
 *         <TableCell>Acme</TableCell>
 *       </TableRow>
 *     </TableBody>
 *   </Table>
 *
 * Wrap in a card-like container (the consumer's responsibility) for the
 * border/shadow shell — the table itself stays unstyled at the wrapper level
 * so the same primitive serves card-tables AND embedded tables.
 */
type TableProps = HTMLAttributes<HTMLTableElement> & {
  /**
   * Namespace under which resizable columns persist their widths to
   * `localStorage` (key: `@eureca/ui:table:<storageId>` — single JSON map
   * `{ <colKey>: <px>, … }`). Each `<TableHead resizable>` also needs a
   * `colKey` to opt in. Without `storageId`, resizes still work but don't
   * survive a reload.
   */
  storageId?: string;
  /**
   * Classes applied to the wrapping `<div>` that gives the table its scroll
   * container (`overflow-x-auto` is baked in). Add `max-h-… overflow-y-auto`
   * here to also get vertical scroll — combining both axes in the same
   * container is required so a `sticky top-0` TableHeader sticks to the
   * scroll-container top instead of the page.
   */
  containerClassName?: string;
};

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, storageId, containerClassName, children, ...props }, forwardedRef) => {
    const internalRef = useRef<HTMLTableElement | null>(null);
    const setRefs = useCallback(
      (node: HTMLTableElement | null) => {
        internalRef.current = node;
        assignRef(forwardedRef, node);
      },
      [forwardedRef]
    );

    const normalizedStorageId = storageId ?? null;
    const [widths, setWidths] = useState<Record<string, number>>(() =>
      readAllStoredWidths(normalizedStorageId)
    );
    // Width of the unkeyed columns (action, checkbox, etc.) captured at
    // freeze time. Combined with the keyed `widths` map, this gives us the
    // exact total width to set on the `<table>` so `table-layout: fixed`
    // honors per-TH widths strictly — `table-layout: fixed` needs the table
    // to have a specified width, otherwise the browser falls back to auto
    // layout (which re-derives column widths from content and prevents
    // shrinking below the largest cell).
    const [unkeyedWidthsTotal, setUnkeyedWidthsTotal] = useState<number>(0);
    const hasFrozenWidths = Object.keys(widths).length > 0;
    const totalWidth = hasFrozenWidths
      ? Object.values(widths).reduce((s, w) => s + w, 0) + unkeyedWidthsTotal
      : null;

    // When widths are restored from localStorage on mount, `unkeyedWidthsTotal`
    // starts at 0 (it's only captured inside `freezeColumnsFromDOM`). Measure
    // the unkeyed columns from the rendered DOM so the inline table width is
    // complete before the user touches anything.
    useEffect(() => {
      if (!hasFrozenWidths || unkeyedWidthsTotal > 0) return;
      const table = internalRef.current;
      if (!table) return;
      const allThs = table.querySelectorAll<HTMLTableCellElement>('thead > tr:first-child > th');
      let unkeyedTotal = 0;
      allThs.forEach((th) => {
        if (!th.getAttribute('data-col-key')) {
          unkeyedTotal += th.offsetWidth;
        }
      });
      if (unkeyedTotal > 0) setUnkeyedWidthsTotal(unkeyedTotal);
    }, [hasFrozenWidths, unkeyedWidthsTotal]);

    const ctx = useMemo<TableStorageState>(
      () => ({
        storageId: normalizedStorageId,
        widths,
        hasFrozenWidths,
        setColumnWidth: (key, width) => {
          setWidths((curr) => {
            const next = { ...curr, [key]: width };
            writeAllStoredWidths(normalizedStorageId, next);
            return next;
          });
        },
        freezeColumnsFromDOM: () => {
          if (hasFrozenWidths) return;
          const table = internalRef.current;
          if (!table) return;
          // Capture every column's currently rendered width. Keyed columns
          // go into the resizable `widths` map; unkeyed columns (e.g. the
          // `w-12` checkbox / action cells) contribute their fixed width to
          // the total so the table's `width` reflects ALL columns.
          const allThs = table.querySelectorAll<HTMLTableCellElement>(
            'thead > tr:first-child > th'
          );
          const measured: Record<string, number> = {};
          let unkeyedTotal = 0;
          allThs.forEach((th) => {
            const key = th.getAttribute('data-col-key');
            if (key) {
              measured[key] = th.offsetWidth;
            } else {
              unkeyedTotal += th.offsetWidth;
            }
          });
          if (Object.keys(measured).length > 0) {
            setWidths(measured);
            setUnkeyedWidthsTotal(unkeyedTotal);
            writeAllStoredWidths(normalizedStorageId, measured);
          }
        },
      }),
      [normalizedStorageId, widths, hasFrozenWidths]
    );

    return (
      <TableStorageContext.Provider value={ctx}>
        <div className={cn('w-full overflow-x-auto', containerClassName)}>
          <table
            ref={setRefs}
            // Before any column is frozen: `w-full` — table fills container,
            // columns distribute via table-fixed.
            // After freeze: inline `width: <sum>px` — `table-layout: fixed`
            // requires a specified table width to honor per-TH widths, so we
            // explicitly size the table to the sum of all column widths. Any
            // other approach (`w-max`, `w-auto`) lets the browser re-derive
            // column widths from content and breaks shrinking below the
            // largest cell.
            className={cn('border-collapse text-sm', hasFrozenWidths ? null : 'w-full', className)}
            style={totalWidth != null ? { width: `${totalWidth}px` } : undefined}
            {...props}
          >
            {children}
          </table>
        </div>
      </TableStorageContext.Provider>
    );
  }
);
Table.displayName = 'Table';

const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn('', className)} {...props} />
);
TableHeader.displayName = 'TableHeader';

const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('', className)} {...props} />
);
TableBody.displayName = 'TableBody';

const TableFooter = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn('border-t border-border bg-muted font-medium text-foreground', className)}
      {...props}
    />
  )
);
TableFooter.displayName = 'TableFooter';

const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-border text-foreground last:border-b-0',
        'transition-colors hover:bg-muted',
        'data-[state=selected]:bg-accent',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

type TableHeadProps = ThHTMLAttributes<HTMLTableCellElement> & {
  /**
   * When `true`, renders a drag handle on the right edge that lets the user
   * resize this column. Most useful in `table-fixed` layouts; in auto-layout
   * tables the resize still works but other columns may shift around it.
   */
  resizable?: boolean;
  /**
   * Stable identifier for this column within the parent `<Table storageId>`.
   * When BOTH are set, the resized width persists across reloads via
   * `localStorage`. Without `storageId`, resizes are session-scoped; without
   * `colKey`, the column does not participate in the freeze-on-resize logic.
   */
  colKey?: string;
};

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  (
    { className, scope = 'col', onMouseEnter, resizable, colKey, style, children, ...props },
    ref
  ) => {
    const ctx = useContext(TableStorageContext);
    const width = colKey && ctx ? ctx.widths[colKey] : undefined;
    const wrapsText = isTextLike(children);

    function handleMouseEnter(event: MouseEvent<HTMLTableCellElement>) {
      // For text content the inner span owns the tooltip (it's the element
      // that actually clips). For rich content (no inner wrapper) we fall
      // back to the cell itself, which may have overflow-hidden via a
      // consumer-supplied className.
      if (!wrapsText) {
        syncOverflowTitle(event.currentTarget);
      }
      onMouseEnter?.(event);
    }

    function handleResizeStart(event: PointerEvent<HTMLSpanElement>) {
      // Prevent the th's own mousedown handlers (e.g. sort triggers) from
      // firing while the user is grabbing the handle.
      event.preventDefault();
      event.stopPropagation();
      const cell = event.currentTarget.parentElement;
      if (!(cell instanceof HTMLTableCellElement)) return;
      // Coordinated freeze-then-resize requires the context + a colKey so
      // we know where to store this column's width. Without it, fall back to
      // a silent no-op — the cursor still changes but the column won't move.
      if (!colKey || !ctx) return;
      // Re-bind to locals so TypeScript keeps the non-null narrowing inside
      // the `onMove` closure (otherwise nested-function reads of `ctx`/`colKey`
      // see them as possibly-null again).
      const key = colKey;
      const tableState = ctx;

      // Snapshot every column's current width and switch the table to `w-max`
      // BEFORE the user starts moving. From here on, growing this column
      // grows the table; siblings hold the widths they had at this instant.
      tableState.freezeColumnsFromDOM();

      const startX = event.clientX;
      const startWidth = cell.offsetWidth;
      let latest = startWidth;
      let lastClientX = event.clientX;

      // Walk up the DOM to find the closest horizontally-scrollable ancestor —
      // that's the wrapper from `<Table>`'s `containerClassName`. Used to
      // auto-scroll only when the user drags PAST the container's right edge
      // (growing a column wider than the viewport) — never on shrink.
      const scrollContainer = findHorizontalScrollAncestor(cell);
      const scrollStart = scrollContainer?.scrollLeft ?? 0;

      // Max scroll velocity per frame at the deepest overshoot.
      const MAX_VELOCITY = 24;
      let autoScrollFrame: number | null = null;

      function applyWidth() {
        // Account for any scrolling that happened during the drag so the
        // cell's right edge tracks the cursor's viewport position even after
        // the container scrolled to follow it.
        const scrollDelta = (scrollContainer?.scrollLeft ?? 0) - scrollStart;
        latest = Math.max(MIN_RESIZABLE_WIDTH, startWidth + (lastClientX - startX) + scrollDelta);
        tableState.setColumnWidth(key, latest);
      }

      function autoScrollTick() {
        autoScrollFrame = null;
        if (!scrollContainer) return;
        const rect = scrollContainer.getBoundingClientRect();
        // ONLY scroll when the cursor has actually crossed past the right
        // edge. The previous "edge zone" implementation fired while the user
        // was still inside the container — including when they were dragging
        // LEFT to shrink near the left edge — and shrank the column faster
        // than they wanted.
        const overshoot = lastClientX - rect.right;
        if (overshoot > 0) {
          const dx = Math.min(MAX_VELOCITY, Math.ceil(overshoot / 2));
          scrollContainer.scrollLeft += dx;
          applyWidth();
          autoScrollFrame = window.requestAnimationFrame(autoScrollTick);
        }
      }

      function onMove(ev: globalThis.PointerEvent) {
        lastClientX = ev.clientX;
        applyWidth();
        if (autoScrollFrame == null) {
          autoScrollFrame = window.requestAnimationFrame(autoScrollTick);
        }
      }
      function onUp() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (autoScrollFrame != null) {
          window.cancelAnimationFrame(autoScrollFrame);
          autoScrollFrame = null;
        }
      }
      // Lock cursor + suppress text selection globally while dragging so the
      // pointer can leave the handle without losing the col-resize affordance.
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    }

    return (
      <th
        ref={ref}
        scope={scope}
        // `data-col-key` is what `freezeColumnsFromDOM` greps for when it
        // measures the row — every column that wants to be frozen needs one.
        data-col-key={colKey}
        className={cn(
          // `px-3` (12px each side) keeps a small floor when the cell is
          // resized down — `box-sizing: border-box` (Tailwind preflight)
          // means the cell never renders narrower than padding+border, so
          // wider padding directly caps how small a resized column can go.
          // `overflow-hidden` clips rich content (badges, buttons, glyphs)
          // when the user shrinks the column past its content's natural
          // width; without it, the content visibly spills into the next
          // column. Text children get their own ellipsis via the inner
          // `block truncate` span below.
          'h-12 overflow-hidden border-b border-border px-3 text-left text-sm font-medium text-muted-foreground whitespace-nowrap',
          // Header hover is intentionally lighter than the body rows'
          // (`hover:bg-muted`): the header is chrome, not data, so a
          // half-strength muted tint is enough feedback without competing
          // with the row hover. `group` lets the resize handle reveal its
          // indicator on hover from anywhere in the cell, not just the 12px
          // grab zone.
          'group transition-colors hover:bg-muted/60',
          resizable && 'relative',
          // Checkbox cells: zero the padding, become a positioning context,
          // and absolute-center the checkbox via transform. `text-align`
          // and `align-middle` aren't pixel-perfect with `inline-flex`
          // children (line-box + baseline drift); the transform approach
          // is independent of line-height and the child's display type.
          '[&:has([role=checkbox])]:relative [&:has([role=checkbox])]:px-0',
          '[&>[data-slot=checkbox]]:absolute [&>[data-slot=checkbox]]:top-1/2 [&>[data-slot=checkbox]]:left-1/2 [&>[data-slot=checkbox]]:-translate-x-1/2 [&>[data-slot=checkbox]]:-translate-y-1/2',
          className
        )}
        style={width != null ? { ...style, width: `${width}px` } : style}
        {...props}
        onMouseEnter={handleMouseEnter}
      >
        {isTextLike(children) ? (
          <span
            className="block truncate"
            onMouseEnter={(event) => syncOverflowTitle(event.currentTarget)}
          >
            {children}
          </span>
        ) : (
          children
        )}
        {resizable ? (
          // 12px transparent hit area (w-3) holding a hairline `::before`
          // indicator anchored to the right edge. The line stays hidden until
          // the header cell is hovered (group-hover), then shows as a 1px
          // inset divider in the neutral `border` token — the same separator
          // vocabulary used across the table, so the affordance reads as part
          // of the design rather than a loud accent. Only while actively
          // dragging does it commit to the brand `primary` and thicken to a
          // 2px full-height line, confirming the grab.
          <span
            aria-hidden
            onPointerDown={handleResizeStart}
            className="absolute right-0 top-0 z-10 h-full w-3 touch-none cursor-col-resize select-none before:absolute before:right-0 before:inset-y-1.5 before:w-px before:rounded-full before:bg-transparent before:transition-colors before:content-[''] group-hover:before:bg-border active:before:inset-y-0 active:before:w-0.5 active:before:bg-primary"
          />
        ) : null}
      </th>
    );
  }
);
TableHead.displayName = 'TableHead';

// The cell itself stays unclipped — rich content (badges, buttons, chips)
// renders at its natural width and is allowed to overflow when a column is
// shrunk past the content's size. Only direct text children get the
// `truncate` treatment, applied via the auto-inserted inner span so the CSS
// ellipsis ("…") only kicks in on text overflow.
const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, onMouseEnter, children, ...props }, ref) => {
    const wrapsText = isTextLike(children);

    function handleMouseEnter(event: MouseEvent<HTMLTableCellElement>) {
      // Only the text-wrapping span actually clips; rich-content cells have
      // no overflow-hidden, so a cell-level title would always be stale.
      if (!wrapsText) {
        syncOverflowTitle(event.currentTarget);
      }
      onMouseEnter?.(event);
    }

    return (
      <td
        ref={ref}
        className={cn(
          // `px-3` matches TableHead — wider padding caps how small a
          // resized column can shrink (border-box floor). `overflow-hidden`
          // clips rich row content (badges, action buttons, etc.) at the
          // cell boundary so a shrunk column doesn't bleed into siblings;
          // text children get truncation via the inner `block truncate`
          // span below.
          'h-12 overflow-hidden px-3 text-sm text-foreground align-middle whitespace-nowrap',
          // Match TableHead — checkbox cells become positioning contexts and
          // absolute-center the toggle so it lines up under the header.
          '[&:has([role=checkbox])]:relative [&:has([role=checkbox])]:px-0',
          '[&>[data-slot=checkbox]]:absolute [&>[data-slot=checkbox]]:top-1/2 [&>[data-slot=checkbox]]:left-1/2 [&>[data-slot=checkbox]]:-translate-x-1/2 [&>[data-slot=checkbox]]:-translate-y-1/2',
          className
        )}
        {...props}
        onMouseEnter={handleMouseEnter}
      >
        {wrapsText ? (
          <span
            className="block truncate"
            onMouseEnter={(event) => syncOverflowTitle(event.currentTarget)}
          >
            {children}
          </span>
        ) : (
          children
        )}
      </td>
    );
  }
);
TableCell.displayName = 'TableCell';

const TableCaption = forwardRef<HTMLTableCaptionElement, HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  )
);
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell, TableCaption };
